import type { EnvironmentApi } from '@env0/environment-service/api';
import type { ErrorSurfacingOptions } from 'services/api-client/client';
import useApiClient from 'hooks/use-api-client';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useCurrentOrganizationId } from 'hooks/use-current-organization-id';

const useCacheKeys = () => {
  const orgId = useCurrentOrganizationId();

  const baseKey = [orgId, 'projects-environments-drift-statuses-counts-by-organization'] as const;
  const cacheKeys = {
    byDriftStatuses: (driftStatus: EnvironmentApi.DriftStatus[]) =>
      [...baseKey, 'drift-statuses-counts', { driftStatus }] as const
  } as const;

  return cacheKeys;
};

export const useProjectsEnvironmentsStatusesCountsByOrganization = (
  driftStatus: EnvironmentApi.DriftStatus[],
  { hideNotification, enabled, staleTime }: ErrorSurfacingOptions & { enabled?: boolean; staleTime?: number } = {}
) => {
  const apiClient = useApiClient();
  const { byDriftStatuses } = useCacheKeys();
  // eslint-disable-next-line @tanstack/query/exhaustive-deps
  return useQuery({
    enabled,
    staleTime,
    // hideNotification only changes how a failure is surfaced, not the data, so it must stay out of the
    // queryKey — keying on it would give two callers separate caches for an identical response
    queryKey: byDriftStatuses(driftStatus),
    queryFn: ({ queryKey: [organizationId] }) =>
      apiClient.environments.getProjectsEnvironmentsStatusesCountsByOrganization(
        {
          organizationId,
          driftStatus: driftStatus.toString(),
          isActive: 'true'
        },
        { hideNotification }
      )
  });
};

export const useClearProjectsEnvironmentsStatusesCountsByOrganization = (driftStatus: EnvironmentApi.DriftStatus[]) => {
  const queryClient = useQueryClient();
  const { byDriftStatuses } = useCacheKeys();
  return () => queryClient.invalidateQueries({ queryKey: byDriftStatuses(driftStatus) });
};
